import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { greenBar, redBar, yellowBar } from "../../../helpers/constants";
import { setSelectedGoal } from "../../../slices/Employee/survey-CRR.slice";
import Pill from "./Pill";

const MilIndicator = ({ graphData }) => {
  const dispatch = useDispatch();
  const { selectedGoal } = useSelector((state) => state.CRRsurvey);
  const { goalsData } = useSelector((state) => state.goals);

  const getPillColor = (status) => {
    if (status === "Incomplete") return yellowBar;
    if (status === "Yes") return greenBar;
    return redBar;
  };

  const goalsList = useMemo(
    () => (Array.isArray(goalsData) ? goalsData.filter((g) => g?.id) : []),
    [goalsData]
  );

  const mils = Array.isArray(graphData)
    ? [...graphData].sort((a, b) => (a?.mil_no ?? 0) - (b?.mil_no ?? 0))
    : [];

  // Goals often only exist under MIL-1 — show MIL row + one shared goals row
  const goals = mils.flatMap((mil) =>
    [...(mil?.goals || [])]
      .filter((goal) => goal?.goal_id)
      .map((goal) => ({ ...goal, mil_no: mil.mil_no }))
  );
  const uniqueGoals = [...goals]
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
    .filter(
      (goal, index, arr) =>
        arr.findIndex((g) => g.goal_id === goal.goal_id) === index
    );

  const findGoalById = useCallback(
    (goalId) => goalsList.find((g) => g.id === goalId),
    [goalsList]
  );

  const findMilSection = useCallback(
    (milNo) => {
      const milSection = goalsList.find(
        (g) => Number(g.mil_no) === Number(milNo) && !g.is_goal
      );
      if (milSection) return milSection;
      return goalsList.find((g) => Number(g.mil_no) === Number(milNo));
    },
    [goalsList]
  );

  const findGoalByOrder = useCallback(
    (order, goalId) => {
      if (goalId) {
        const byId = findGoalById(goalId);
        if (byId) return byId;
      }
      return goalsList.find(
        (g) => g.is_goal && Number(g.order) === Number(order)
      );
    },
    [findGoalById, goalsList]
  );

  const handleMilClick = (milNo) => {
    const target = findMilSection(milNo);
    if (target) dispatch(setSelectedGoal(target));
  };

  const handleGoalClick = (goal) => {
    const target = findGoalByOrder(goal?.order, goal?.goal_id);
    if (target) dispatch(setSelectedGoal(target));
  };

  const activeMilNo = selectedGoal?.mil_no;
  const activeGoalId = selectedGoal?.is_goal ? selectedGoal?.id : null;
  const activeGoalOrder = selectedGoal?.is_goal ? selectedGoal?.order : null;

  return (
    <div className="mil-wrapper">
      <div className="mil-row" aria-label="Maturity indicator levels">
        {mils.map((mil) => {
          const canNavigate = Boolean(findMilSection(mil.mil_no));
          return (
            <Pill
              key={mil.mil_no}
              text={`MIL-${mil.mil_no}`}
              styles={getPillColor(mil?.status)}
              tooltipText={`Go to Maturity Indicator Level-${mil.mil_no}`}
              active={Number(activeMilNo) === Number(mil.mil_no) && !activeGoalId}
              onClick={canNavigate ? () => handleMilClick(mil.mil_no) : undefined}
              disabled={!canNavigate}
            />
          );
        })}
      </div>
      {uniqueGoals.length > 0 && (
        <div className="goal-container" aria-label="Goals">
          {uniqueGoals.map((goal) => {
            const canNavigate = Boolean(
              findGoalByOrder(goal?.order, goal?.goal_id)
            );
            const isActive =
              (activeGoalId &&
                Number(activeGoalId) === Number(goal.goal_id)) ||
              (activeGoalOrder != null &&
                Number(activeGoalOrder) === Number(goal.order));
            return (
              <Pill
                key={goal.goal_id || goal.order}
                text={`G${goal?.order}`}
                styles={getPillColor(goal?.status)}
                tooltipText={`Go to Goal ${goal?.order}`}
                active={Boolean(isActive)}
                onClick={
                  canNavigate ? () => handleGoalClick(goal) : undefined
                }
                disabled={!canNavigate}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MilIndicator;
